// ─── PSE payer contract ───────────────────────────────────────────────────────
// The shape the PSE network demands of whoever authorizes the debit, expressed once. Both entry points —
// `/app/payins` (Colombia tab) and the COP wallet deposit modal — build their form from this module, so the
// two screens cannot drift on what the network accepts.
//
// Everything here is pure: no React, no i18n. Messages arrive as strings so the rules can be unit-tested
// without a translation runtime, and so the same schema serves three locales.

import { z } from "zod";

/**
 * Colombian document short codes the backend maps to the network's own literals.
 *
 * This is the network's catalog, not a product decision: `document_type` travels to the provider verbatim, so
 * a code outside this list is rejected downstream — after the payin row already exists.
 */
export const PSE_DOCUMENT_TYPES = ["CC", "CE", "NIT", "TI", "PA"] as const;

/** A natural person may use any document EXCEPT NIT. */
export const PSE_NATURAL_DOCUMENT_TYPES = ["CC", "CE", "TI", "PA"] as const;

/** A legal entity is always a NIT — the network offers no alternative, so this list has exactly one entry. */
export const PSE_LEGAL_DOCUMENT_TYPES = ["NIT"] as const;

export const PSE_PERSON_TYPES = ["natural", "juridica"] as const;

export type PseDocumentType = (typeof PSE_DOCUMENT_TYPES)[number];
export type PsePersonType = (typeof PSE_PERSON_TYPES)[number];

/**
 * Field maximums are the PSE network's own. Exceeding one is a provider-side rejection, not a UI nicety,
 * which is why they are enforced by the schema and mirrored as `maxLength` on the inputs.
 */
export const PSE_FIELD_MAX = {
  documentNumber: 15,
  payerName: 64,
  email: 120,
  address: 64,
} as const;

/** Copy the schema needs. Supplied by the caller so this module stays free of the translation runtime. */
export interface PsePayerMessages {
  required: string;
  documentInvalid: string;
  emailInvalid: string;
  phoneInvalid: string;
  /** Called with the offending maximum so the caller can interpolate it into its own sentence. */
  maxLength: (max: number) => string;
}

/** Which document types the person type admits — drives the select's options. */
export function pseDocumentTypesFor(personType: PsePersonType): readonly PseDocumentType[] {
  return personType === "juridica" ? PSE_LEGAL_DOCUMENT_TYPES : PSE_NATURAL_DOCUMENT_TYPES;
}

/**
 * The document type that must be selected for this person type, given whatever is selected now.
 *
 * The network's rule, in one place: a legal entity is always NIT, and a natural person is never NIT. Any
 * other combination is left alone — switching from CC to CE must not be undone.
 */
export function reconcilePseDocumentType(
  personType: PsePersonType,
  current: PseDocumentType,
): PseDocumentType {
  if (personType === "juridica") return "NIT";
  return current === "NIT" ? "CC" : current;
}

/** How the person type is spelled on the wire (`payer.user_type`). */
export function psePayerUserType(personType: PsePersonType): "person" | "company" {
  return personType === "juridica" ? "company" : "person";
}

/**
 * The payer form's validation contract.
 *
 * Phone and document are digit-only shapes rather than free text because the payload strips separators before
 * sending: validating the stripped shape here is what keeps the error next to the field instead of arriving
 * as a provider rejection.
 */
export function createPsePayerSchema(messages: PsePayerMessages) {
  return z.object({
    personType: z.enum(PSE_PERSON_TYPES),
    documentType: z.enum(PSE_DOCUMENT_TYPES),
    documentNumber: z.string().regex(/^\d{4,15}$/, messages.documentInvalid),
    payerName: z
      .string()
      .trim()
      .min(2, messages.required)
      .max(PSE_FIELD_MAX.payerName, messages.maxLength(PSE_FIELD_MAX.payerName)),
    email: z
      .string()
      .trim()
      .email(messages.emailInvalid)
      .max(PSE_FIELD_MAX.email, messages.maxLength(PSE_FIELD_MAX.email)),
    // PSE only settles in Colombia, so the payer is reachable only on a Colombian mobile: 10 digits from 3.
    phone: z.string().regex(/^3\d{9}$/, messages.phoneInvalid),
    address: z
      .string()
      .trim()
      .min(3, messages.required)
      .max(PSE_FIELD_MAX.address, messages.maxLength(PSE_FIELD_MAX.address)),
    bankCode: z.string().min(1, messages.required),
  });
}

export type PsePayerValues = z.infer<ReturnType<typeof createPsePayerSchema>>;
